import { CardProps, Text } from "@chakra-ui/react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FORM_SCHOOL_RANK_SUB_STATUS,
  OFFER_STATUS,
  WAITLIST_STATUS,
} from "src/constants";
import {
  GetDashboardFormSubstatusResponse,
  GetGradeConfigByEnrollmentPeriodResponse,
  GetSchoolsByEnrollmentPeriodResponse,
} from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import * as Format from "src/services/format";
import { isNonEmptyString } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { ChartTooltip } from "./ChartTooltip";
import { DashboardChart } from "./DashboardChart";
import { Grade, GradesMultiSelectInput } from "./GradesMultiSelectInput";
import { School, SchoolsMultiSelectInput } from "./SchoolsMultiSelectInput";
import { WrappedLabel } from "./WrappedLabel";

type Props = {
  formTemplateId: string;
  exportId?: string;
  remoteData: [
    RD.RemoteData<unknown, GetDashboardFormSubstatusResponse>,
    RD.RemoteData<unknown, GetSchoolsByEnrollmentPeriodResponse>,
    RD.RemoteData<unknown, GetGradeConfigByEnrollmentPeriodResponse>
  ];
  cardProps?: CardProps;
};
const ASPECT_RATIO = 1.5;
export const AdmissionByStatusChart: React.FC<Props> = ({
  formTemplateId,
  exportId,
  remoteData: [formSubstatusRD, schoolsRD, gradesRD],
  cardProps,
}) => {
  const [selectedSchools, setSelectedSchools] = React.useState<School[] | null>(
    null
  );
  const [selectedGrades, setSelectedGrades] = React.useState<Grade[] | null>(
    null
  );

  const allSchools = React.useMemo(() => {
    if (schoolsRD.hasData()) {
      return schoolsRD.data.data;
    }
    return [];
  }, [schoolsRD]);

  const allGrades = React.useMemo(() => {
    if (gradesRD.hasData()) {
      return gradesRD.data.data;
    }
    return [];
  }, [gradesRD]);

  const { glossary, glossaryTransform } = useGlossary();

  const data = React.useMemo(() => {
    return formSubstatusRD.map((formSubstatus) => {
      return [
        {
          name: glossaryTransform(
            WAITLIST_STATUS[GQL.waitlist_status_enum.Waitlisted].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Waitlisted",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#D69E2E",
        },
        {
          name: glossaryTransform(
            WAITLIST_STATUS[GQL.waitlist_status_enum.Withdrawn].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Withdrawn",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#718096",
        },
        {
          name: glossaryTransform(
            WAITLIST_STATUS[GQL.waitlist_status_enum.Removed].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Removed",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#718096",
        },
        {
          name: glossaryTransform(
            FORM_SCHOOL_RANK_SUB_STATUS[
              GQL.form_school_rank_status_enum.NotConsidered
            ].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "NotConsidered",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#CBD5E0",
        },
        {
          name: glossaryTransform(
            OFFER_STATUS[GQL.offer_status_enum.Offered].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Offered",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#63B3ED",
        },
        {
          name: glossaryTransform(
            OFFER_STATUS[GQL.offer_status_enum.Accepted].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Accepted",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#38A169",
        },
        {
          name: glossaryTransform(
            OFFER_STATUS[GQL.offer_status_enum.Declined].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Declined",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#718096",
        },
        {
          name: glossaryTransform(
            OFFER_STATUS[GQL.offer_status_enum.Revoked].label
          ),
          value: calculateSubstatusTotal(
            formTemplateId,
            "Revoked",
            formSubstatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#718096",
        },
      ];
    });
  }, [
    formSubstatusRD,
    glossaryTransform,
    formTemplateId,
    selectedSchools,
    selectedGrades,
  ]);

  return (
    <DashboardChart
      aspectRatio={ASPECT_RATIO}
      cardProps={cardProps}
      title={glossaryTransform("Admissions by status")}
      caption={glossary`Admissions by school, not unique by student`}
      data={data}
      exportId={exportId}
      exportFileName="admissions-by-status"
      filters={
        <>
          <SchoolsMultiSelectInput
            remoteData={schoolsRD}
            onChange={setSelectedSchools}
            selectedSchools={selectedSchools ?? allSchools}
          />
          <GradesMultiSelectInput
            remoteData={gradesRD}
            onChange={setSelectedGrades}
            selectedGrades={selectedGrades ?? allGrades}
          />
        </>
      }
    >
      {(data) => (
        <BarChart
          style={{
            width: "100%",
            aspectRatio: ASPECT_RATIO,
          }}
          responsive
          data={data}
          margin={{
            top: 24,
            right: 0,
            left: 0,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical />
          <XAxis
            dataKey="name"
            tickLine={false}
            axisLine={false}
            interval={0}
            tick={(props) => <WrappedLabel {...props} />}
          />
          <YAxis
            width={40}
            tickLine={false}
            axisLine={false}
            domain={[0, "auto"]}
            tickFormatter={(value: number) =>
              Intl.NumberFormat("en", { notation: "compact" }).format(value)
            }
          />
          <Tooltip<number, string>
            content={({ active, payload, label }) => {
              const value = payload[0]?.payload?.value;
              const color = payload[0]?.payload?.color;
              if (active && payload && value) {
                return (
                  <ChartTooltip
                    accentColor={color ?? ""}
                    title={String(label ?? "")}
                  >
                    <Text fontSize="0.9rem">
                      <strong>{Format.formatWholeNumber(value)}</strong>{" "}
                      {value === 1 ? "response" : "responses"}
                    </Text>
                  </ChartTooltip>
                );
              }
              return null;
            }}
          />
          <Bar
            dataKey="value"
            radius={[0, 0, 0, 0]}
            shape={(props: any) => (
              <Rectangle {...props} fill={props?.payload?.color ?? "#8B97AA"} />
            )}
          >
            <LabelList dataKey="value" position="top" offset={8} />
          </Bar>
        </BarChart>
      )}
    </DashboardChart>
  );
};

export function calculateSubstatusTotal(
  formTemplateId: string,
  substatus: Substatus,
  formSubstatus: GetDashboardFormSubstatusResponse,
  selectedSchools: School[] | null,
  selectedGrades: Grade[] | null
): number {
  let total = 0;
  for (const row of formSubstatus.data) {
    if (
      row.form_template_id !== formTemplateId ||
      row.substatus !== substatus
    ) {
      continue;
    }

    // null means "all selected" — skip that filter entirely
    // empty array means no schools selected — only include rows without a school
    if (selectedSchools !== null) {
      if (isNonEmptyString(row.school_id)) {
        if (selectedSchools.length === 0) {
          continue;
        }
        if (!selectedSchools.find((s) => s.id === row.school_id)) {
          continue;
        }
      } else {
        if (selectedSchools.length > 0) {
          continue;
        }
      }
    }

    if (selectedGrades !== null) {
      if (isNonEmptyString(row.grade_config_id)) {
        if (selectedGrades.length === 0) {
          continue;
        }
        if (!selectedGrades.find((g) => g.id === row.grade_config_id)) {
          continue;
        }
      } else {
        if (selectedGrades.length > 0) {
          continue;
        }
      }
    }

    let count = 0;
    if (row === undefined) continue;
    if (row.count === null) continue;
    if (typeof row.count === "number") {
      count = row.count;
    } else {
      count = parseInt(row.count);
    }
    total += count;
  }
  return total;
}

type Substatus =
  | `${GQL.waitlist_status_enum}`
  | `${GQL.offer_status_enum}`
  | `${GQL.form_school_rank_status_enum}`;
